const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/selectors/casesSelectors.ts';
import Imm from 'immutable';
import { createSelector } from 'reselect';
import { selectCurrentPatientNr } from './patientsSelectors';
import { CommonStoreState } from '../reducers';
import { CaseData } from '@cistec/common_web/defs/kisim';
import { actionLoadCases } from '@cistec/common_web/actions/casesActions';
import { getData, useRemoteData2 } from '@cistec/common_web/utils/redux4Requests';
import { ReducerState } from '@cistec/common_web/defs/actionTypes';
import { useMemo } from 'react';

const emptyMap = Imm.Map<string, any>();

const selectReducerState = (state: CommonStoreState): ReducerState => state.cases;

// simple selectors
export const casesMapRoot = (state: CommonStoreState): Imm.Map<string, any> =>
    state.cases.get('map') || Imm.Map<string, any>();

export const selectCaseRecordsRoot = (state: CommonStoreState): Imm.Map<string, any> => state.cases.get('cases');

export const selectOpenKgRoot = (state: CommonStoreState): Imm.Map<string, any> => state.cases.get('openkg');

export const currentPatientsCurrentCase = (state: CommonStoreState) =>
    state.cases.getIn(['map', selectCurrentPatientNr(state), 'current']);

export const isLoadingDischargeMgmt = (state: CommonStoreState): boolean =>
    (state.cases.getIn(['dischargeMgmt', 'loading']) || 0) > 0;

export const dischargeMgmtRecordsRoot = (state: CommonStoreState): Imm.Map<string, any> =>
    state.cases.getIn(['dischargeMgmt', 'records']);

// compound selectors
export const selectCurrentCaseNr = createSelector(
    [casesMapRoot, selectCurrentPatientNr],
    (_casesMapRoot, _currentPatient): string => {
        if (_casesMapRoot) {
            return _casesMapRoot.getIn([_currentPatient, 'current']) || '';
        }
        return '';
    }
);
export const selectCurrentCaseData = createSelector(
    [selectCaseRecordsRoot, selectCurrentCaseNr],
    (_caseRecordsRoot: Imm.Map<string, any>, _currentCaseNr: string): Imm.Map<string, any> => {
        if (_caseRecordsRoot) {
            return _caseRecordsRoot.get(_currentCaseNr) || emptyMap;
        }
        return emptyMap; // avoid creating a new instance every time
    }
);

export const makeSelectCases = (patnr: string) =>
    createSelector([selectReducerState], (state: ReducerState): CaseData[] | undefined => {
        return getData(state, ['raw', patnr]);
    });

export const makeSelectCase = (patnr: string, casenr: string) =>
    createSelector([makeSelectCases(patnr)], cases => {
        return cases ? cases.find(caseData => caseData.PATNR === patnr && caseData.FALLNR === casenr) : undefined;
    });

export const currentCaseDataType = createSelector(
    selectCurrentCaseData,
    (state: any, type: string) => type,
    (currentCaseData: Imm.Map<string, any>, type: string) => {
        const fieldMap: any = {
            fallnr: 'FALLNR',
            patnr: 'PATNR',
        };
        const field = fieldMap[type] || '';
        return currentCaseData.get(field) || '';
    }
);

export const useCase = (patnr: string, casenr: string) => {
    const select = useMemo(() => makeSelectCase(patnr, casenr), [patnr, casenr]);

    return useRemoteData2('cases', {
        canRequest: !!patnr,
        request: dispatch => dispatch(actionLoadCases(patnr, false)),
        keys: ['raw', patnr],
        select,
    });
};

export const useCases = (patnr: string, shouldRequest?: boolean) => {
    const select = useMemo(() => makeSelectCases(patnr), [patnr]);

    return useRemoteData2('cases', {
        canRequest: !!patnr,
        request: dispatch => dispatch(actionLoadCases(patnr, false)),
        keys: ['raw', patnr],
        select,
        shouldRequest,
    });
};
